import {
  booleanAttribute,
  computed,
  Directive,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { ShSize } from '../models';
import { SH_FIELD } from '../field/field.context';

/**
 * Shared base of every `sh-*` form control: one value, three ways to bind it.
 *
 *   <sh-input [(value)]="name" />              signal model
 *   <sh-input [(ngModel)]="name" />            template-driven forms
 *   <sh-input [formControl]="nameControl" />   reactive forms
 *
 * The value lives in `value` (a `model()`); the `ControlValueAccessor`
 * methods read and write it. A subclass calls `commit(next)` when the user
 * changes something: it compares first and then emits to the model and the
 * form API exactly once per real change. `writeValue` never emits to the
 * form API, so nothing can loop on init (see the freeze trap in the design
 * handoff): never re-emit from `writeValue`, and never build new arrays or
 * objects in a getter that feeds a PrimeNG input; use a `computed()`.
 *
 * Inside an `sh-field` the control picks up its id (label `for`), the
 * read-only lock and the error state through the `SH_FIELD` context.
 *
 * Subclasses provide `NG_VALUE_ACCESSOR` themselves (with `forwardRef`),
 * because the provider must name the concrete class.
 */
// eslint-disable-next-line @angular-eslint/directive-class-suffix
@Directive()
export abstract class ShFormControl<T> implements ControlValueAccessor {
  readonly value = model<T | null>(null);
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly readonly = input(false, { transform: booleanAttribute });
  readonly invalid = input(false, { transform: booleanAttribute });
  /** Translation key or plain text. */
  readonly placeholder = input<string>('');
  readonly size = input<ShSize>('md');
  /** Translation key or plain text; for controls without a visible label. */
  readonly ariaLabel = input<string>();
  /** The native element's id; set by `sh-field` when not given. */
  readonly inputId = input<string>();

  protected readonly field = inject(SH_FIELD, { optional: true });

  // `disabled` merged with the form API's `setDisabledState`.
  private readonly _formDisabled = signal(false);
  protected readonly isDisabled = computed(
    () => this.disabled() || this._formDisabled()
  );
  protected readonly isReadonly = computed(
    () => this.readonly() || !!this.field?.readonly()
  );
  protected readonly isInvalid = computed(
    () => this.invalid() || !!this.field?.error()
  );
  // Outside an `sh-field` (and without `inputId`) the control still needs an
  // id: a checkbox or toggle with its own `label` links it through `for`,
  // and without one, clicking the label did nothing.
  private static nextAutoId = 0;
  private readonly _autoId = `sh-control-${++ShFormControl.nextAutoId}`;
  protected readonly controlId = computed(
    () => this.inputId() ?? this.field?.controlId ?? this._autoId
  );

  private _onChange: (value: T | null) => void = () => undefined;
  private _onTouched: () => void = () => undefined;

  writeValue(value: T | null | undefined): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._formDisabled.set(isDisabled);
  }

  /** Value equality for `commit`; arrays and dates override. */
  protected equals(a: T | null, b: T | null): boolean {
    return a === b;
  }

  /** The user changed the value: emit once, and only when it really differs. */
  protected commit(next: T | null | undefined): void {
    const value = next ?? null;
    if (this.equals(this.value() ?? null, value)) return;
    this.value.set(value);
    this._onChange(value);
  }

  protected markTouched(): void {
    this._onTouched();
  }
}
